import axios from 'lib/axios'
import {
  CompatibleMetric,
  JiraDataFilterRow,
  JiraFieldsCatalog,
  JiraFilterPreview,
  JiraOptionsResponse,
  JiraProject,
  OnboardingCreate,
  ProductOnboardingView,
  ProductSpec
} from 'models'

const basePath = '/api/v4/products'

/* Consolidated client for the v4 products API and its product-onboarding sub-resource.
 * Onboarding methods (get/submit-step/complete/projects) are added as the wizard
 * tickets need them. */
export const ProductsServiceV4 = {
  getSpec: async (productSlug: string): Promise<ProductSpec> => {
    const { data } = await axios.post<ProductSpec>(`${basePath}/spec`, {
      product_spec: { product_slug: productSlug }
    })
    return data
  },

  createOnboarding: async (onboarding: OnboardingCreate): Promise<ProductOnboardingView> => {
    const { data } = await axios.post<ProductOnboardingView>(`${basePath}/onboarding`, {
      onboarding_create: onboarding
    })
    return data
  },

  getOnboarding: async (instanceName: string): Promise<ProductOnboardingView> => {
    const { data } = await axios.get<ProductOnboardingView>(`${basePath}/onboarding/${instanceName}`)
    return data
  },

  submitStep: async (
    instanceName: string,
    stepKey: string,
    body: { revision: number; data?: Record<string, any>; skip?: boolean }
  ): Promise<ProductOnboardingView> => {
    const { data } = await axios.post<ProductOnboardingView>(
      `${basePath}/onboarding/${instanceName}/steps/${stepKey}`,
      body
    )
    return data
  },

  /* Idempotent: completing an already-completed onboarding is safe to retry. */
  completeOnboarding: async (instanceName: string, revision: number): Promise<ProductOnboardingView> => {
    const { data } = await axios.post<ProductOnboardingView>(`${basePath}/onboarding/${instanceName}/complete`, {
      revision
    })
    return data
  },

  getCompatibleMetrics: async (instanceName: string): Promise<CompatibleMetric[]> => {
    const { data } = await axios.post<{ metrics: CompatibleMetric[] }>(`${basePath}/${instanceName}/compatible-metrics`)
    return data.metrics
  },

  configureMetrics: async (instanceName: string, metricSlugs: string[]) =>
    axios.post(`${basePath}/${instanceName}/configure-metrics`, {
      tenant_slug: 'tenant_slug',
      metrics_slugs: metricSlugs
    }),

  getJiraProjects: async (productId: string): Promise<JiraProject[]> => {
    const { data } = await axios.get<JiraProject[]>(`${basePath}/${productId}/jira/projects`)
    return data
  },

  getJiraFieldsCatalog: async (productId: string): Promise<JiraFieldsCatalog> => {
    const { data } = await axios.get<JiraFieldsCatalog>(`${basePath}/${productId}/jira/fields`)
    return data
  },

  /* Option list for a Data Configuration select. `resource` is the endpoint
   * segment (labels, components, users, issue-types, resolutions, statuses),
   * scoped to the projects picked in Connector Setup; `priorities` covers the
   * whole Jira instance. */
  getJiraOptions: async (productId: string, resource: string): Promise<JiraOptionsResponse> => {
    const { data } = await axios.get<JiraOptionsResponse>(`${basePath}/${productId}/jira/${resource}`)
    return data
  },

  /* Sample the issues a filter payload matches, before the step is applied. */
  previewJiraIssues: async (productId: string, filters: JiraDataFilterRow[]): Promise<JiraFilterPreview> => {
    const { data } = await axios.post<JiraFilterPreview>(`${basePath}/${productId}/jira/preview`, { filters })
    return data
  }
}
